// EasyPost credential field names in this file must match its carrier_types
// metadata (GET /v2/carrier_types); print-easypost-carrier-fields prints them.
import { CanadaPostManifestMode } from "@redotech/redo-fulfillment-carrier-account-common/canada-post";
import { DhlEcsDistributionCenter } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import {
  GOFO_ORIGIN_HUBS,
  GofoByocaOriginHub,
} from "@redotech/redo-fulfillment-carrier-common/gofo-hubs";
import { UNIUNI_US_WAREHOUSES } from "@redotech/redo-fulfillment-carrier-common/uniuni-warehouses";
import {
  discriminatedUnion,
  literal,
  object,
  string,
  enum as zEnum,
  infer as zInfer,
} from "zod";
import {
  CarrierAccountType,
  FedexCrossBorderHubOrigin,
  RedoCarrierName,
  SendcloudRegion,
  UspsDirectRateType,
} from "./carrier-account";

export const FirstMileCarrierAccountCredentials = object({
  account_number: string(),
  username: string(),
  password: string(),
});

export type IFirstMileCarrierAccountCredentials = zInfer<
  typeof FirstMileCarrierAccountCredentials
>;

export const AustraliaPostCarrierAccountCredentials = object({
  account_number: string(),
  api_key: string(),
  api_secret: string(),
});

export type IAustraliaPostCarrierAccountCredentials = zInfer<
  typeof AustraliaPostCarrierAccountCredentials
>;

/**
 * Sent to EasyPost, as opposed to filled in by the merchant. Australia Post
 * requires this mode until Redo supports manifesting purchased labels.
 */
export type IAustraliaPostEasypostCredentials =
  IAustraliaPostCarrierAccountCredentials & { print_as_you_go: "true" };

export const CanadaPostCarrierAccountCredentials = object({
  api_key: string(),
  contract_id: string(),
  customer_number: string(),
  has_credit_card: string(),
});

export type ICanadaPostCarrierAccountCredentials = zInfer<
  typeof CanadaPostCarrierAccountCredentials
>;

/**
 * Sent to EasyPost, as opposed to filled in by the merchant. Without
 * `not_platform` Canada Post rejects every rate request as "Not authorized".
 */
export type ICanadaPostEasypostCredentials =
  ICanadaPostCarrierAccountCredentials & { not_platform: "true" };

export const CanadaPostDirectCarrierAccountCredentials = object({
  api_key: string().trim().min(1),
  api_secret: string().trim().min(1),
  customer_number: string().trim().min(1),
  contract_number: string().trim().min(1),
  manifest_mode: zEnum(CanadaPostManifestMode),
});

export type ICanadaPostDirectCarrierAccountCredentials = zInfer<
  typeof CanadaPostDirectCarrierAccountCredentials
>;

export const VehoCarrierAccountCredentials = object({ api_key: string() });

export type IVehoCarrierAccountCredentials = zInfer<
  typeof VehoCarrierAccountCredentials
>;

export const ApcCarrierAccountCredentials = object({
  shipper_id: string(),
  password: string(),
  facility: string(),
});

export type IApcCarrierAccountCredentials = zInfer<
  typeof ApcCarrierAccountCredentials
>;

export const DHLCarrierAccountCredentials = object({
  distribution_center: zEnum(DhlEcsDistributionCenter),
  pickup_id: string(),
  client_id: string(),
  client_secret: string(),
});

export type IDHLCarrierAccountCredentials = zInfer<
  typeof DHLCarrierAccountCredentials
>;

export const DHLExpressCarrierAccountCredentials = object({
  account_number: string(),
  country_code: string(),
  password: string().optional(),
  site_id: string().optional(),
});

export type IDHLExpressCarrierAccountCredentials = zInfer<
  typeof DHLExpressCarrierAccountCredentials
>;

export const UPSCarrierAccountCredentials = object({
  account_number: string(),
});

export type IUPSCarrierAccountCredentials = zInfer<
  typeof UPSCarrierAccountCredentials
>;

export const UPSSurepostCarrierAccountCredentials = object({
  account_number: string(),
});

export type IUPSSurepostCarrierAccountCredentials = zInfer<
  typeof UPSSurepostCarrierAccountCredentials
>;

export const UPSMailInnovationsCarrierAccountCredentials = object({
  account_number: string(),
  user_id: string(),
  password: string(),
  customer_id: string().optional(),
  customer_guid: string().optional(),
});

export type IUPSMailInnovationsCarrierAccountCredentials = zInfer<
  typeof UPSMailInnovationsCarrierAccountCredentials
>;

export const USPSCarrierAccountCredentials = object({
  company_name: string(),
  address_street: string(),
  address_city: string(),
  address_state: string(),
  address_zip: string(),
  phone: string(),
  email: string(),
  shipper_id: string().optional(),
});

export type IUspsCarrierAccountCredentials = zInfer<
  typeof USPSCarrierAccountCredentials
>;

export const FedexCrossBorderCarrierAccountCredentials = object({
  api_key: string(),
  connect_hub_origin: zEnum(FedexCrossBorderHubOrigin),
});

export const defaultCarrierCredentials = object({
  company_name: string(),
  address_street: string(),
  address_city: string(),
  address_state: string(),
  address_zip: string(),
  address_country: string(),
  phone: string(),
  email: string(),
});

export type IDefaultCarrierCredentials = zInfer<
  typeof defaultCarrierCredentials
>;

export type IFedexCrossBorderCarrierAccountCredentials = zInfer<
  typeof FedexCrossBorderCarrierAccountCredentials
>;

export const FedexCarrierAccountCredentials = object({
  account_number: string(),
  shipping_city: string(),
  shipping_country_code: string(),
  shipping_postal_code: string(),
  shipping_state: string(),
  shipping_streets: string(),
  corporate_city: string(),
  corporate_company_name: string(),
  corporate_country_code: string(),
  corporate_email_address: string(),
  corporate_first_name: string(),
  corporate_job_title: string(),
  corporate_last_name: string(),
  corporate_phone_number: string(),
  corporate_postal_code: string(),
  corporate_state: string(),
  corporate_streets: string(),
});

export type IFedexCarrierAccountCredentials = zInfer<
  typeof FedexCarrierAccountCredentials
>;

export const FedexSmartpostCarrierAccountCredentials = object({
  account_number: string(),
  hub_id: string(),
  shipping_city: string(),
  shipping_country_code: string(),
  shipping_postal_code: string(),
  shipping_state: string(),
  shipping_streets: string(),
  corporate_city: string(),
  corporate_company_name: string(),
  corporate_country_code: string(),
  corporate_email_address: string(),
  corporate_first_name: string(),
  corporate_job_title: string(),
  corporate_last_name: string(),
  corporate_phone_number: string(),
  corporate_postal_code: string(),
  corporate_state: string(),
  corporate_streets: string(),
});

export type IFedexSmartpostCarrierAccountCredentials = zInfer<
  typeof FedexSmartpostCarrierAccountCredentials
>;

export const RoyalMailCarrierAccountCredentials = object({
  account_number: string(),
  posting_location_number: string(),
  username: string(),
});

export type IRoyalMailCarrierAccountCredentials = zInfer<
  typeof RoyalMailCarrierAccountCredentials
>;

export const RoyalMailV3CarrierAccountCredentials = object({
  account_number: string(),
  oba_email_address: string(),
  posting_location_number: string(),
  oba_access_code: string().optional(),
  registered_billing_postcode: string().optional(),
  contact_name: string(),
  contact_phone: string(),
  dispatch_street1: string(),
  dispatch_street2: string().optional(),
  dispatch_city: string(),
  dispatch_zip: string(),
  dispatch_state: string(),
  dispatch_country: string(),
});

export type IRoyalMailV3CarrierAccountCredentials = zInfer<
  typeof RoyalMailV3CarrierAccountCredentials
>;

export const SekoOmniParcelCredentials = object({
  api_access_key: string(),
  return_api_access_key: string(),
});

export const SekoOmniParcelCarrierAccountCredentials = object({
  outboundProductionApiKey: string(),
  returnsProductionApiKey: string(),
  outboundTestApiKey: string(),
  returnsTestApiKey: string(),
});

export type ISekoOmniParcelCredentials = zInfer<
  typeof SekoOmniParcelCredentials
>;

export type ISekoOmniParcelCarrierAccountCredentials = zInfer<
  typeof SekoOmniParcelCarrierAccountCredentials
>;

export const GlsCarrierAccountCredentials = object({
  account_number: string(),
  password: string(),
  username: string(),
});

export type IGlsCarrierAccountCredentials = zInfer<
  typeof GlsCarrierAccountCredentials
>;

export const AsendiaCarrierAccountCredentials = object({
  account_number: string(),
  api_key: string(),
  carrier_facility: string().optional(),
  password: string(),
  sub_account_number: string().optional(),
  username: string(),
});

export type IAsendiaCarrierAccountCredentials = zInfer<
  typeof AsendiaCarrierAccountCredentials
>;

export const SpeeDeeCarrierAccountCredentials = object({
  account_number: string(),
});

export type ISpeeDeeCarrierAccountCredentials = zInfer<
  typeof SpeeDeeCarrierAccountCredentials
>;

export const AmazonCarrierAccountCredentials = object({});

export type IAmazonCarrierAccountCredentials = zInfer<
  typeof AmazonCarrierAccountCredentials
>;

export const UspsShipCarrierAccountCredentials = object({
  client_id: string(),
  client_secret: string(),
  account_number: string(),
  customer_registration_id: string(),
  mailer_id: string(),
  manifest_id: string(),
});

export type IUspsShipCarrierAccountCredentials = zInfer<
  typeof UspsShipCarrierAccountCredentials
>;

export const PassportGlobalCarrierAccountCredentials = object({
  api_key: string(),
  company_name: string(),
  email: string(),
  name: string(),
  phone: string(),
});

export type IPassportGlobalCarrierAccountCredentials = zInfer<
  typeof PassportGlobalCarrierAccountCredentials
>;

export const EPostGlobalCarrierAccountCredentials = object({
  account_number: string(),
});

export type IEPostGlobalCarrierAccountCredentials = zInfer<
  typeof EPostGlobalCarrierAccountCredentials
>;

export const OnTracCarrierAccountCredentials = object({
  api_id: string(),
  api_key: string(),
  facility: string(),
  injection_city: string(),
  injection_contact: string(),
  injection_country: string(),
  injection_facility_code: string(),
  injection_instruction: string(),
  injection_phone: string(),
  injection_state: string(),
  injection_street1: string(),
  injection_street2: string().optional(),
  injection_zip: string(),
  pickup_type: string(),
});

export type IOnTracCarrierAccountCredentials = zInfer<
  typeof OnTracCarrierAccountCredentials
>;

export const PurolatorCarrierAccountCredentials = object({
  account_number: string(),
  api_key: string(),
  password: string(),
});

export type IPurolatorCarrierAccountCredentials = zInfer<
  typeof PurolatorCarrierAccountCredentials
>;

export const ETAExpressCarrierAccountCredentials = object({
  api_key: string(),
});

export type IETAExpressCarrierAccountCredentials = zInfer<
  typeof ETAExpressCarrierAccountCredentials
>;

export const UspsDirectCarrierAccountCredentials = object({
  account_number: string(),
  customer_registration_id: string(),
  mailer_id: string(),
  manifest_mailer_id: string(),
  rate_type: zEnum(UspsDirectRateType),
});

export type IUspsDirectCarrierAccountCredentials = zInfer<
  typeof UspsDirectCarrierAccountCredentials
>;

export type UniUniPickupWarehouse =
  (typeof UNIUNI_US_WAREHOUSES)[number]["code"];

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const UniUniPickupWarehouse = UNIUNI_US_WAREHOUSES.map(
  (w) => w.code,
) as [UniUniPickupWarehouse, ...UniUniPickupWarehouse[]];

export const UniUniCarrierAccountCredentials = object({
  client_id: string(),
  client_secret: string(),
  customer_number: string(),
  pickup_warehouse: zEnum(UniUniPickupWarehouse),
});

export type IUniUniCarrierAccountCredentials = zInfer<
  typeof UniUniCarrierAccountCredentials
>;

export const StallionExpressCarrierAccountCredentials = object({
  api_token: string(),
});

export type IStallionExpressCarrierAccountCredentials = zInfer<
  typeof StallionExpressCarrierAccountCredentials
>;

export const EcomExpressCarrierAccountCredentials = object({
  account_number: string(),
  api_key: string(),
});

export type IEcomExpressCarrierAccountCredentials = zInfer<
  typeof EcomExpressCarrierAccountCredentials
>;

export const FreightWireCarrierAccountCredentials = object({
  api_key: string(),
  customer_id: string(),
});

export type IFreightWireCarrierAccountCredentials = zInfer<
  typeof FreightWireCarrierAccountCredentials
>;

export const ChitChatsCarrierAccountCredentials = object({
  client_id: string(),
  api_token: string(),
});

export type IChitChatsCarrierAccountCredentials = zInfer<
  typeof ChitChatsCarrierAccountCredentials
>;

export const SendcloudCarrierAccountCredentials = object({
  region: zEnum(SendcloudRegion),
  public_key: string(),
  secret_key: string(),
});

export type ISendcloudCarrierAccountCredentials = zInfer<
  typeof SendcloudCarrierAccountCredentials
>;

export const CanparCarrierAccountCredentials = object({
  account_number: string(),
  password: string(),
  username: string(),
});

export type ICanparCarrierAccountCredentials = zInfer<
  typeof CanparCarrierAccountCredentials
>;

// DPD NL issues a Delis ID (EasyPost's `username`) and a password.
export const DpdNlCarrierAccountCredentials = object({
  username: string(),
  password: string(),
});

export type IDpdNlCarrierAccountCredentials = zInfer<
  typeof DpdNlCarrierAccountCredentials
>;

export const JitsuCarrierAccountCredentials = object({ api_key: string() });

export type GofoOriginHub = (typeof GOFO_ORIGIN_HUBS)[number]["code"];

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const GofoOriginHub = GOFO_ORIGIN_HUBS.map((h) => h.code) as [
  GofoOriginHub,
  ...GofoOriginHub[],
];

export const GofoCarrierAccountCredentials = object({
  api_key: string().trim().min(1),
  user_id: string().trim().min(1),
  origin_hub: zEnum(GofoOriginHub),
});

export type IGofoCarrierAccountCredentials = zInfer<
  typeof GofoCarrierAccountCredentials
>;

/** A GOFO username login, connected directly for flagged teams and through EasyPost otherwise. */
export const GofoByocaCarrierAccountCredentials = object({
  username: string().min(1),
  password: string().min(1),
  origin_hub: zEnum(GofoByocaOriginHub).optional(),
  replacesCarrierAccountId: string().optional(),
});

export type IGofoByocaCarrierAccountCredentials = zInfer<
  typeof GofoByocaCarrierAccountCredentials
>;

export const P2PCarrierAccountCredentials = object({
  api_key: string().trim().min(1),
  customer_id: string().trim().min(1),
  username: string().trim().min(1),
});

export type IP2PCarrierAccountCredentials = zInfer<
  typeof P2PCarrierAccountCredentials
>;

export type IJitsuCarrierAccountCredentials = zInfer<
  typeof JitsuCarrierAccountCredentials
>;

export const CreateCarrierAccount = object({
  name: string(),
  carrierAccountType: zEnum(CarrierAccountType),
  data: discriminatedUnion("redoCarrierName", [
    object({
      redoCarrierName: literal(RedoCarrierName.DHL_ECS),
      ...DHLCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.DHL_EXPRESS),
      ...DHLExpressCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.UPS),
      ...UPSCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.UPS_SUREPOST),
      ...UPSSurepostCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.UPS_MAIL_INNOVATIONS),
      ...UPSMailInnovationsCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.USPS),
      ...USPSCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.FEDEX_CROSS_BORDER),
      ...FedexCrossBorderCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.FEDEX),
      ...FedexCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.FEDEX_SMARTPOST),
      ...FedexSmartpostCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.VEHO),
      ...VehoCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.APC),
      ...ApcCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.FIRST_MILE),
      ...FirstMileCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.AUSTRALIA_POST),
      ...AustraliaPostCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.CANADA_POST),
      ...CanadaPostCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.CANADA_POST_DIRECT),
      ...CanadaPostDirectCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.ROYAL_MAIL),
      ...RoyalMailCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.ROYAL_MAIL_V3),
      ...RoyalMailV3CarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.SEKO_OMNIPARCEL),
      ...SekoOmniParcelCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.GLS),
      ...GlsCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.ASENDIA),
      ...AsendiaCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.SPEE_DEE),
      ...SpeeDeeCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.AMAZON),
      ...AmazonCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.USPS_SHIP),
      ...UspsShipCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.PASSPORT_GLOBAL),
      ...PassportGlobalCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.EPOST_GLOBAL),
      ...EPostGlobalCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.ETA_EXPRESS),
      ...ETAExpressCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.USPS_DIRECT),
      ...UspsDirectCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.ONTRAC),
      ...OnTracCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.PUROLATOR),
      ...PurolatorCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.UNIUNI),
      ...UniUniCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.SENDCLOUD),
      ...SendcloudCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.STALLION_EXPRESS),
      ...StallionExpressCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.ECOM_EXPRESS),
      ...EcomExpressCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.FREIGHTWIRE),
      ...FreightWireCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.CHITCHATS),
      ...ChitChatsCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.CANPAR_EXPRESS),
      ...CanparCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.DPD_NL),
      ...DpdNlCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.JITSU),
      ...JitsuCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.GOFO),
      ...GofoCarrierAccountCredentials.shape,
    }),
    object({
      redoCarrierName: literal(RedoCarrierName.P2P_GLOBAL),
      ...P2PCarrierAccountCredentials.shape,
    }),
  ]).or(
    object({
      redoCarrierName: literal(RedoCarrierName.GOFO),
      ...GofoByocaCarrierAccountCredentials.shape,
    }),
  ),
});

export type ICreateCarrierAccount = zInfer<typeof CreateCarrierAccount>;
